import React, { useEffect, useState } from 'react';
import {
  Button,
  Alert,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import { useProfile } from '../../context/userContext';
import { useAuth0 } from '@auth0/auth0-react';
import { useAuth } from '../../context/authContext';
import { useNavigate } from 'react-router-dom';
import { useOrganization } from '../../context/OrganizationContext';
import styles from '../../styles/OrganizationPlans.module.css';

const OrganizationPlans = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [billingInterval, setBillingInterval] = useState('month');
  const { profile } = useProfile();
  const { loginWithRedirect } = useAuth0();
  const { authenticated, token } = useAuth();
  const { isOrganizationUser, individualSubscription, userContext, currentOrganization } = useOrganization();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchOrgPlans = async () => {
      try {
        // Clear any old mock data from sessionStorage
        const oldPlanData = sessionStorage.getItem('organizationPlanData');
        if (oldPlanData) {
          try {
            const parsed = JSON.parse(oldPlanData);
            // Check if it has mock price IDs (they don't start with 'price_')
            if (parsed.priceId && (parsed.priceId.includes('_team_') || parsed.priceId.includes('_business_'))) {
              sessionStorage.removeItem('organizationPlanData');
            }
          } catch (e) {
            // Invalid JSON, clear it
            sessionStorage.removeItem('organizationPlanData');
          }
        }


        // Fetch organization plans from Stripe via API
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/prices`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            metadataKey: 'type',
            metadataValues: ['organization']
          })
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to fetch plans: ${response.status} ${errorText}`);
        }

        const data = await response.json();

        if (!data || data.length === 0) {
          setError('No organization plans available. Please create organization products in Stripe with metadata type=organization');
          setLoading(false);
          return;
        }

        // Sort plans by display_order
        const sortedPlans = data.sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
        setPlans(sortedPlans);
        setLoading(false);
      } catch (error) {
        setError(error.message || 'Failed to load organization plans. Please try again later.');
        setLoading(false);
      }
    };

    fetchOrgPlans();
  }, []);

  const handleBillingChange = (event, newInterval) => {
    if (newInterval !== null) {
      setBillingInterval(newInterval);
    }
  };

  const handleGetStarted = async (plan) => {
    // Check if it's Custom Enterprise plan (no prices = contact sales)
    const isCustomEnterprise = plan.prices.length === 0 || plan.name === 'Custom Enterprise';

    if (isCustomEnterprise) {
      // Navigate to contact us page for Custom Enterprise
      navigate('/contactus');
      return;
    }

    // Get the selected price for the plan
    const selectedPrice = plan.prices.find(p => p.recurring?.interval === billingInterval);

    if (!authenticated) {
      // Smart routing: Pass plan selection to Auth0 via appState
      // After Auth0 login, user will be redirected to /signup/organization with plan data

      // Store intent in sessionStorage BEFORE Auth0 redirect as backup
      sessionStorage.setItem('auth0RedirectTo', '/signup/organization');
      sessionStorage.setItem('organizationPlanData', JSON.stringify({
        planId: plan.id,
        planName: plan.name,
        priceId: selectedPrice?.id,
        billingInterval: billingInterval,
        amount: selectedPrice?.unit_amount
      }));

      loginWithRedirect({
        authorizationParams: {
          redirect_uri: `${window.location.origin}/home`,
          screen_hint: 'signup',
          // Custom parameter that Auth0 Action can read from event.request.query
          signup_type: 'organization'
        },
        appState: {
          returnTo: '/signup/organization',
          intent: 'create_organization',
          planData: {
            planId: plan.id,
            planName: plan.name,
            priceId: selectedPrice?.id,
            billingInterval: billingInterval,
            amount: selectedPrice?.unit_amount
          }
        }
      });
      return;
    }

    // Check if user already has an organization - if yes, upgrade/change subscription
    if (isOrganizationUser && currentOrganization?.subscription) {
      if (!currentOrganization.subscription.stripeCustomerId) {
        alert('Unable to manage subscription. Stripe customer ID is missing. Please contact support.');
        return;
      }

      try {
        // Redirect to Stripe billing portal for subscription management
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/organization/subscription/portal`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            stripeCustomerId: currentOrganization.subscription.stripeCustomerId
          })
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to create portal session: ${response.status}`);
        }

        const { url } = await response.json();
        window.location.href = url;
      } catch (error) {
        alert('Unable to open subscription management. Please try again.');
      }
      return;
    }

    // User is authenticated but no organization - navigate to organization signup
    navigate('/signup/organization', {
      state: {
        selectedPlan: plan,
        billingInterval: billingInterval,
        priceId: selectedPrice?.id
      }
    });
  };

  const calculateAnnualSavings = (monthlyPrice, annualPrice) => {
    const monthlyCost = (monthlyPrice / 100) * 12;
    const annualCost = annualPrice / 100;
    const savings = monthlyCost - annualCost;
    const percentage = Math.round((savings / monthlyCost) * 100);
    return percentage;
  };

  const formatPrice = (amount, interval) => {
    if (amount === 0) return 'Custom';
    const price = interval === 'year' ? amount / 12 / 100 : amount / 100;
    return `$${Math.round(price)}`;
  };

  // Calculate average annual savings across all plans
  const getAverageSavings = () => {
    const plansWithBothPrices = plans.filter(plan => {
      const hasMonthly = plan.prices.some(p => p.recurring?.interval === 'month');
      const hasAnnual = plan.prices.some(p => p.recurring?.interval === 'year');
      return hasMonthly && hasAnnual;
    });

    if (plansWithBothPrices.length === 0) return 0;

    const totalSavings = plansWithBothPrices.reduce((sum, plan) => {
      const monthlyPrice = plan.prices.find(p => p.recurring?.interval === 'month');
      const annualPrice = plan.prices.find(p => p.recurring?.interval === 'year');
      if (monthlyPrice && annualPrice) {
        const savings = calculateAnnualSavings(monthlyPrice.unit_amount, annualPrice.unit_amount);
        return sum + savings;
      }
      return sum;
    }, 0);

    return Math.round(totalSavings / plansWithBothPrices.length);
  };

  const averageSavings = getAverageSavings();

  const renderFeature = (feature, index) => (
    <li key={index} className={styles['feature-item']}>
      <CheckCircleIcon
        className={styles['icon-check']}
        aria-hidden="true"
        fontSize="small"
      />
      <span>{feature}</span>
    </li>
  );

  const renderPlanCard = (plan, isHighlighted = false) => {
    const selectedPrice = plan.prices.find(p => p.recurring?.interval === billingInterval);
    const monthlyPrice = plan.prices.find(p => p.recurring?.interval === 'month');
    const annualPrice = plan.prices.find(p => p.recurring?.interval === 'year');
    // Check if it's Custom Enterprise (no prices = contact sales)
    const isCustom = plan.prices.length === 0 || plan.name === 'Custom Enterprise';

    // Check if this is the organization's current plan by comparing Stripe Product IDs
    const currentOrgProductId = currentOrganization?.subscription?.stripeProductId;
    const isCurrentOrgPlan = isOrganizationUser && currentOrgProductId && currentOrgProductId === plan.id;

    // Determine button state
    const isUpgrade = !isCurrentOrgPlan && !isCustom && (isOrganizationUser || individualSubscription);

    const savings = monthlyPrice && annualPrice
      ? calculateAnnualSavings(monthlyPrice.unit_amount, annualPrice.unit_amount)
      : 0;

    return (
      <div key={plan.id} className={styles['plan-wrapper']}>
        {isHighlighted && (
          <div className={styles['popular-badge']} aria-label="Most popular plan">
            <StarIcon className={styles['popular-badge-icon']} />
            Most Popular
          </div>
        )}

        <article
          className={`${styles['plan-card']} ${isHighlighted ? styles['plan-highlighted'] : ''} ${isCustom ? styles['plan-custom'] : ''}`}
          aria-label={`${plan.name} organization plan`}
        >
          <header className={styles['plan-header']}>
          <h3 className={styles['plan-name']}>{plan.name}</h3>

          {!isCustom && selectedPrice ? (
            <div className={styles['plan-price']}>
              <span className={styles['price-amount']} aria-label={`Price: ${formatPrice(selectedPrice.unit_amount, billingInterval)} per month`}>
                {formatPrice(selectedPrice.unit_amount, billingInterval)}
              </span>
              <span className={styles['price-period']} aria-hidden="true">/month</span>
              {billingInterval === 'year' && savings > 0 && (
                <div className={styles['savings-badge']} aria-label={`Save ${savings}% with annual billing`}>
                  Save {savings}%
                </div>
              )}
              <div className={styles['trial-badge']}>
                7-day free trial included
              </div>
            </div>
          ) : (
            <div className={styles['plan-price']}>
              <p className={styles['contact-sales-note']}>Contact us for a quote</p>
            </div>
          )}

          <p className={styles['plan-description']}>{plan.description}</p>
        </header>

        <div className={styles['plan-body']}>
          <h4 className={styles['features-heading']}>Features included:</h4>
          <ul className={styles['features-list']} aria-label={`Features for ${plan.name} plan`}>
            {plan.features.map(renderFeature)}
          </ul>
        </div>

        <footer className={styles['plan-footer']}>
          {isCurrentOrgPlan ? (
            <Button
              variant="outlined"
              fullWidth
              disabled
              aria-label="Currently on this plan"
              className={styles['btn-current']}
            >
              Current Plan
            </Button>
          ) : (
            <Button
              variant={isHighlighted ? 'contained' : 'outlined'}
              color="primary"
              fullWidth
              onClick={() => handleGetStarted(plan)}
              aria-label={
                isCustom
                  ? 'Contact sales for custom enterprise plan'
                  : isUpgrade
                    ? `Upgrade to ${plan.name}`
                    : `Start 7-day free trial for ${plan.name}`
              }
              className={styles['btn-get-started']}
            >
              {isCustom ? 'Contact Sales' : isUpgrade ? 'Upgrade' : 'Start 7-Day Free Trial'}
            </Button>
          )}

          <p className={styles['trial-note']} aria-label="Trial details">
            {isCustom || isCurrentOrgPlan ? '\u00A0' : isUpgrade ? 'Upgrade and start 7-day trial' : 'No credit card required'}
          </p>
        </footer>
      </article>
    </div>
    );
  };

  if (loading) {
    return (
      <div className={styles['loading-container']} role="status" aria-live="polite">
        <CircularProgress aria-label="Loading organization plans" />
        <span className="sr-only">Loading organization plans...</span>
      </div>
    );
  }

  if (error) {
    return (
      <Alert severity="error" role="alert" className={styles['error-alert']}>
        {error}
      </Alert>
    );
  }

  return (
    <div className={styles['organization-plans-container']}>
      <div className={styles['plans-intro']}>
        <h2 className={styles['section-title']}>Organization Plans</h2>
        <p className={styles['section-description']}>
          Powerful tools for teams and businesses. All plans include 7-day free trial.
        </p>
      </div>

      {/* Billing Toggle */}
      <div className={styles['billing-toggle-container']}>
        <label id="billing-toggle-label" className={styles['toggle-label']}>
          Billing frequency:
        </label>
        <ToggleButtonGroup
          value={billingInterval}
          exclusive
          onChange={handleBillingChange}
          aria-labelledby="billing-toggle-label"
          className={styles['billing-toggle']}
        >
          <ToggleButton
            value="month"
            aria-label="Monthly billing"
            className={styles['toggle-button']}
          >
            Monthly
          </ToggleButton>
          <ToggleButton
            value="year"
            aria-label={averageSavings > 0 ? `Annual billing - save ${averageSavings} percent` : "Annual billing"}
            className={styles['toggle-button']}
          >
            Annual
            {averageSavings > 0 && (
              <span className={styles['savings-text']} aria-hidden="true">
                (Save {averageSavings}%)
              </span>
            )}
          </ToggleButton>
        </ToggleButtonGroup>
      </div>

      {/* Plans Grid */}
      <div className={styles['plans-grid']} role="list">
        {plans.map((plan, index) => renderPlanCard(plan, index === 0))}
      </div>

      {/* Additional Information */}
      <aside className={styles['plan-benefits']} aria-labelledby="benefits-heading">
        <h3 id="benefits-heading" className={styles['benefits-title']}>
          All organization plans include:
        </h3>
        <ul className={styles['benefits-list']}>
          <li>Everything from Individual Pro plan</li>
          <li>Multi-user collaboration</li>
          <li>Role-based access control</li>
          <li>Organization dashboard</li>
          <li>Centralized billing</li>
          <li>Data migration assistance</li>
        </ul>
      </aside>

      {/* FAQ Link */}
      <div className={styles['help-section']}>
        <p>
          Not sure which plan is right for you?{' '}
          <Button
            variant="text"
            color="primary"
            href="/contactus"
            aria-label="Contact sales for help choosing a plan"
          >
            Talk to sales
          </Button>
        </p>
      </div>
    </div>
  );
};

export default OrganizationPlans;
